import { useCallback } from "react";

import { useTranslation } from "next-i18next";

import {
  BiddingActionPayload,
  PlaceBidPayload,
} from "../contracts/bidding.contracts";
import { bidErrorMessage } from "@/common/utils/bid-errors.utils";
import addToast from "@/toast/addToast";
import { useCreateBid } from "@whoppah/apollo/hooks/catalog/mutations/useCreateBid";
import { useCreateCounterBid } from "@whoppah/apollo/hooks/catalog/mutations/useCreateCounterBid";
import { showModal } from "@whoppah/ui/Modal2";

/**
 * Hook use to initiate the bidding modal.
 * @returns Callback that will trigger the bidding modal.
 */
export const useInitiateBiddingModal = () => {
  const initiateBiddingModal = useCallback(
    async (payload: BiddingActionPayload) => {
      // Response is returned once the modal is closed.
      const response = await showModal("bidding", payload);

      if (response === "info") {
        // Open the about bidding modal and then open the quick bidding modal again.
        await showModal("about-bidding");
        await initiateBiddingModal(payload);
      }
    },
    []
  );

  return initiateBiddingModal;
};

/**
 * Places a bid based on the currently authenticated user's role.
 * If the user is the seller, the hook will place a `counter bid`.
 * If the user is the buyer, the hook will place a `bid`.
 * This callback doesn't have any side effects, it either places a counter bid or a basic bid.
 * @param payload - Bid placement payload.
 * @returns Callback function for placing the bid.
 */
export const usePlaceBid = (payload: PlaceBidPayload) => {
  const { t } = useTranslation();
  const [createBid] = useCreateBid();
  const [createCounterBid] = useCreateCounterBid();

  const placeBid = useCallback(
    async (bidAmount: number) => {
      if (
        !bidAmount ||
        !payload.auctionId ||
        !payload.role ||
        !payload.currency ||
        (payload.role === "seller" && !payload.buyerId)
      ) {
        return;
      }

      try {
        if (payload.role === "buyer") {
          // Buyers always place bids.
          await createBid({
            variables: {
              amount: bidAmount,
              auctionId: payload.auctionId,
              currency: payload.currency,
            },
          });
          return;
        }

        // Sellers always place counter bids.
        await createCounterBid({
          variables: {
            amount: bidAmount,
            auctionId: payload.auctionId,
            buyerId: payload.buyerId,
            currency: payload.currency,
          },
        });
      } catch (e) {
        console.error("placeBid - Error", e);

        addToast(bidErrorMessage(e, t), { appearance: "error" });

        // Re-throw so the bidding modal stays open and the user can retry.
        throw e;
      }
    },
    [payload, createBid, createCounterBid, t]
  );

  return placeBid;
};
